// User roles matching backend enum
export type UserRole = 'SALES_REP' | 'INSIDE_SALES' | 'MANAGER' | 'ADMIN' | 'MATCHING_SUPPORT' | 'DATA_STEWARD' | 'CAMPAIGN_MANAGER' | 'ASSISTANT';

// One row per (user, role) grant from the backend UserRoleAssignment model.
export interface UserRoleAssignment {
  id: string;
  userId: string;
  role: UserRole;
  grantedById: string | null;
  grantedAt: string;
  expiresAt: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  grantedBy?: { id: string; displayName: string | null; email: string };
}

// User model matching backend
export interface User {
  id: string;
  blastUserId: string | null; // null for pending users awaiting first SSO login
  email: string;
  displayName: string | null;
  role: UserRole; // legacy — still emitted during dual-write phase
  roles: UserRole[]; // sourced from UserRoleAssignment
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  emailComposeMode?: 'COMPOSER' | 'MAILTO' | 'GMAIL';
  // Set for an assistant: the sales rep whose book she works. Null/absent otherwise.
  actingFor?: { id: string; displayName: string | null } | null;
  // Admin list view: the sales rep an assistant works for. Null when not an assistant.
  worksFor?: { id: string; displayName: string | null } | null;
  assignedPeopleCount?: number; // present on admin list view
  sweepExempt?: boolean; // CRM-137: exempt from the nightly roster-sweep deactivation
  rosterSeenAt?: string | null; // CRM-137: first time seen as an active rep in a roster sync row
}

// Dev user (simplified for login picker)
export interface DevUser {
  id: string;
  blastUserId: string;
  email: string;
  displayName: string | null;
  role: UserRole;
}

// Auth token response from /dev/auth/token
export interface TokenResponse {
  token: string;
  user: DevUser;
  expiresIn: string;
}

// User list item (for dropdowns and filters)
export interface UserListItem {
  id: string;
  displayName: string | null;
  email: string;
  role: UserRole;
}

// Temperature enum matching backend
export type Temperature = 'FREEZING' | 'COLD' | 'WARM' | 'HOT';

// Stage enum matching backend
export type Stage = 'LEAD' | 'PROSPECT' | 'CUSTOMER' | 'CHURNED';

// Activity type enum matching backend
export type ActivityType = 'CALL' | 'TEXT' | 'EMAIL' | 'IN_PERSON' | 'NOTE' | 'OTHER';

// Activity status enum matching backend
export type ActivityStatus = 'PLANNED' | 'COMPLETED' | 'CANCELLED';

// Meeting location enum matching backend (for IN_PERSON activities)
export type MeetingLocation = 'ON_CAMPUS' | 'CONFERENCE' | 'RESTAURANT' | 'GAME_TOURNAMENT' | 'OTHER';

// Competitor fundraiser types
export type CompetitorPlatform = 'VERTICAL_RAISE' | 'SNAP_RAISE' | 'ETEAM_SPONSOR' | 'SCHOOL_FUNDR';
export type CampaignStatus = 'ACTIVE' | 'ENDED' | 'UNKNOWN';
export type DiscoverySource = 'TWITTER' | 'FACEBOOK' | 'INSTAGRAM' | 'CSV_IMPORT' | 'MANUAL';

export interface CompetitorFundraiser {
  id: string;
  organizationId: string | null;
  sportId: string | null;
  platform: CompetitorPlatform;
  campaignUrl: string | null;
  campaignName: string | null;
  schoolName: string;
  sport: string | null;
  mascot: string | null;
  state: string | null;
  year: number | null;
  goalAmount: number | null;
  raisedAmount: number | null;
  tipsAmount: number | null;
  participantCount: number | null;
  status: CampaignStatus;
  snippet: string | null;
  blastAmount: number | null;
  sourceUrl: string | null;
  sourcePlatform: DiscoverySource;
  discoveredAt: string;
  lastScrapedAt: string | null;
  organization?: { id: string; name: string; state: string | null } | null;
  screenshotCount?: number;
  latestScreenshotUrl?: string | null;
}

export interface FundraiserScreenshot {
  id: string;
  source: 'MANUAL' | 'AUTO';
  capturedAt: string;
  uploadedBy: { id: string; displayName: string | null } | null;
  contentType: string;
  width: number | null;
  height: number | null;
  /** Null when the server could not sign a read URL for this image. */
  url: string | null;
}

export interface FundraiserFilters {
  platform?: CompetitorPlatform;
  state?: string;
  sport?: string;
  year?: number;
  status?: CampaignStatus;
  sourcePlatform?: DiscoverySource;
  isMatched?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FundraiserStats {
  totalCampaigns: number;
  totalRaised: number;
  byPlatform: Record<string, { count: number; totalRaised: number }>;
  avgBlastWinMargin: number | null;
}

// PATCH /api/fundraisers/:id body — mirrors updateFundraiserSchema (validators/fundraiser.ts).
// Field-level auth (routes/fundraisers.ts): organizationId/sportId/raisedAmount/goalAmount/
// year/status are all-roles; sport/state/blastAmount are MANAGER/ADMIN only.
export interface FundraiserUpdatePayload {
  organizationId?: string | null;
  sportId?: string | null;
  raisedAmount?: number | null;
  goalAmount?: number | null;
  year?: number | null;
  blastAmount?: number | null;
  status?: CampaignStatus;
  sport?: string | null;
  state?: string | null;
}

// Mirrors backend FundraiserExtraction (services/llmExtraction.ts), plus the
// platform/year POST /api/fundraisers/extract derives from the URL itself.
export interface FundraiserExtraction {
  schoolName: string | null;
  sport: string | null;
  mascot: string | null;
  state: string | null;
  campaignName: string | null;
  goalAmount: number | null;
  raisedAmount: number | null;
  participantCount: number | null;
  status: 'ACTIVE' | 'ENDED' | null;
  year: number | null;
  fundraiserStartDate: string | null;
  fundraiserEndDate: string | null;
  platform: CompetitorPlatform | null;
}

// 'ok' = page read AND meaningful fields came back; 'failed' covers both an
// unreadable page and a readable page with nothing usable on it — either way
// the form falls back to manual entry.
export type ExtractionStatus = 'ok' | 'failed';

export interface FundraiserExtractionResponse {
  extraction: FundraiserExtraction;
  extractionStatus: ExtractionStatus;
  extractionReason?: string;
}

// Email direction enum matching backend
export type EmailDirection = 'INBOUND' | 'OUTBOUND';

// Gmail sync status enum matching backend
export type SyncStatus = 'CONNECTED' | 'SYNCING' | 'ERROR' | 'DISCONNECTED';

// Task status enum matching backend
export type TaskStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';

// Organization type enum matching backend
export type OrganizationType = 'SCHOOL' | 'CLUB_TEAM' | 'CLUB' | 'LEAGUE' | 'BAND';

// Customer status union (derived from org-level pipeline data)
export type CustomerStatus = 'PROSPECT' | 'ONBOARDED' | 'ACTIVE' | 'SLIPPING' | 'LAPSED';

// Assignment request status/scope enums matching backend
export type AssignmentRequestStatus = 'PENDING' | 'APPROVED' | 'DENIED' | 'HELD' | 'CANCELLED';
export type AssignmentGrantScope = 'COACH' | 'SCHOOL';
export type AssignmentRequestFilter = 'PENDING' | 'HELD' | 'DECIDED';

export interface AssignmentRequestContext {
  currentOwner: {
    coachDirect: { userId: string; displayName: string | null } | null;
    schoolNamedAccount: { userId: string; displayName: string | null } | null;
    isOwnedByOther: boolean;
  };
  fundraiser: {
    lifetimeCount: number;
    lifetimeTotalRaisedCents: number;
    currentSYCount: number;
    priorSYCount: number;
    creditedReps: { salesRepUserId: string; displayName: string | null; fundraiserCount: number; totalRaisedCents: number }[];
    requesterRanFundraisers: boolean;
  };
  requesterActivityCount: number;
}

export interface AssignmentRequestListItem {
  id: string;
  status: AssignmentRequestStatus;
  grantScope: AssignmentGrantScope | null;
  note: string | null;
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
  person: { id: string; firstName: string; lastName: string; position: string | null; temperature: Temperature | null; stage: Stage; blastUserId: string | null };
  organization: { id: string; name: string; city: string | null; state: string | null; type: string; customerStatus: CustomerStatus };
  requestedBy: { id: string; displayName: string | null };
  decidedBy: { id: string; displayName: string | null } | null;
  context?: AssignmentRequestContext;
}

export interface BlastFundraiserRep {
  id: string;
  blastFundraiserId: string;
  blastUserId: string;
  salesRepUserId: string | null;
  blastUserDisplayName: string | null;
  isPrimary: boolean;
  activeBegin: string | null;     // ISO string from API
  activeEnd: string | null;       // ISO string; null = currently active on this fundraiser
  salesRep: {
    id: string;
    displayName: string | null;
  } | null;
}

export interface BlastFundraiser {
  id: string;
  blastFundraiserId: string;
  blastTeamId: string;
  organizationId: string;
  teamId: string | null;
  blastFinancialAdminUserId: string | null;
  launchedAt: string | null;          // ISO string from API
  athleteCount: number | null;
  participationPct: string | null;    // Decimal — string from API
  totalContactsRequired: number | null;
  totalContactsEntered: number | null;
  totalRaisedCents: string | null;    // BigInt — string from API
  totalTeamNetCents: string | null;
  donationCount: number | null;
  donorCount: number | null;
  avgGiftCents: string | null;
  recurringRaisedCents: string | null;
  oneTimeRaisedCents: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  reps: BlastFundraiserRep[];
}

export interface OrgDonationRollupView {
  periodType: 'LIFETIME' | 'SCHOOL_YEAR' | 'CALENDAR_YEAR';
  periodKey: string;
  raisedCents: string; teamNetCents: string;
  donationCount: number; donorCount: number; avgGiftCents: string;
  recurringRaisedCents: string; oneTimeRaisedCents: string; campaignCount: number;
}

// Gender enum matching backend
export type Gender = 'BOYS' | 'GIRLS' | 'COED';

// Season enum matching backend
export type Season = 'FALL' | 'WINTER' | 'SPRING' | 'SUMMER';

// Sport summary (as returned in person/org responses)
export interface SportSummary {
  id: string;
  name: string;
  gender: Gender;
  season: Season;
}

// BlastSport summary (from /api/integration/sports for dropdown sourcing)
export interface BlastSportSummary {
  blastSportId: number;
  name: string;
  season: Season | null;
}

// Team summary (as returned in person/org responses)
export interface TeamSummary {
  id: string;
  level: string;
  blastTeamId: string | null;
}

// Organization model matching backend
export interface Organization {
  id: string;
  blastSchoolId: string | null;
  blastClubId: string | null;
  blastBandId: string | null;
  clubType: string | null;
  blastTeamId: string | null;
  name: string;
  type: OrganizationType;
  state: string | null;
  city: string | null;
  address: string | null;
  zip: string | null;
  phone: string | null;
  website: string | null;
  district: string | null;
  isPrivate: boolean;
  conference: string | null;
  division: string | null;
  ncesId: string | null;
  ncesSchoolType: string | null;
  ncesIsCharter: boolean | null;
  ncesGradeLow: string | null;
  ncesGradeHigh: string | null;
  ncesLevel: string | null;
  ncesEnrollment: number | null;
  ncesDataYear: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Organization list item (subset for table display)
export interface OrganizationListItem {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  type: OrganizationType;
  isPrivate: boolean;
  ncesLevel: string | null;
  district: string | null;
  blastSchoolId: string | null;
  conference: string | null;
  division: string | null;
  customerStatus: CustomerStatus;
  lastActivityAt?: string | null;
  lastFundraiserLaunchedAt: string | null;
  lastFundraiserTeamDisplay: string | null;
  lastFundraiserAmountCents: string | null;
  lifetimeFundraiserCount: number;
  lifetimeTotalRaisedCents: string;
  currentSchoolYearFundraiserCount: number;
  priorSchoolYearFundraiserCount: number;
  assignedRep: { id: string; displayName: string | null } | null;
  // CRM-115 list-card fields (also on /preview rows)
  ncesEnrollment?: number | null;
  coachCount?: number;
  tags?: import('./tag').Tag[];
}

// Organization search result (for typeahead)
export interface OrganizationSearchResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  isPrivate: boolean;
  ncesLevel: string | null;
}

// Pagination metadata
export interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Organization counts for segment pills.
// Matches GET /api/organizations/counts response (Task 2): total + per-state
// engagement counts + the two segment counters used by the filter sheet.
export interface OrganizationCounts {
  total: number;
  PROSPECT: number;
  ONBOARDED: number;
  ACTIVE: number;
  SLIPPING: number;
  LAPSED: number;
  neverVisited: number;
  hasCoaches: number;
}

// Organization list filters
export interface OrganizationFilters {
  search?: string;
  state?: string;
  city?: string;
  segment?: string;
  isPrivate?: boolean;
  ncesLevel?: string;
  hasBlastLink?: boolean;
  assignedTo?: 'me' | 'unassigned';
  customerStatus?: CustomerStatus[];
  orgTypes?: string[];
  sortBy?: 'name' | 'city' | 'state' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

// Organization update payload (CRM fields only)
export interface OrganizationUpdate {
  conference?: string | null;
  division?: string | null;
  blastSchoolId?: string | null;
}

// Person linked to organization (from org detail API response — org-level people only)
export interface LinkedPerson {
  id: string;
  role: string | null;
  isPrimary: boolean;
  endDate: string | null; // ISO string. null = current link; set = former (end-dated by recency)
  managedBySync?: boolean; // true = attached by Blast sync/import; false/absent = rep-added. Always set by the org-detail endpoint.
  person: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    crmFirstName: string | null;
    crmLastName: string | null;
    crmEmail: string | null;
    crmPhone: string | null;
    position: string | null;
    blastUserId: string | null;
    convertedAt: string | null;
  };
}

// Sport with teams and people (from org detail API response)
export interface SportWithTeams {
  id: string;
  name: string;
  gender: Gender;
  season: Season;
  isSeasonOverridden: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  teams: Array<{
    id: string;
    level: string;
    blastTeamId: string | null;
    rosterCount: number | null;
    isActive: boolean;
    lastActiveSchoolYear: number | null; // latest Blast school_year this team's bucket was fielded
    people: LinkedPerson[];
  }>;
  people: LinkedPerson[]; // Sport-level people (no team)
  _count: {
    teams: number;
    people: number;
  };
}

// Minimal sport shape for the "no context" AddFundraiserModal sport picker —
// deliberately not SportWithTeams; that type drags in teams/people the picker
// never renders.
export interface OrgSportOption {
  id: string;
  name: string;
  gender: Gender;
}

// Full organization with sport/team hierarchy
export type MunchFundCampaignStatus = 'draft' | 'active' | 'ended' | 'archived';

// Mirrors the munchFundCampaigns projection of GET /api/organizations/:id.
export interface MunchFundCampaign {
  id: string;
  munchFundCampaignId: string;
  title: string | null;
  kind: 'fundraiser' | 'house' | null;
  status: MunchFundCampaignStatus | null;
  goalCents: string | null;       // BigInt as string
  raisedCents: string | null;     // BigInt as string
  teamAmountCents: string | null; // BigInt as string
  teamShareBps: number | null;    // 5000 = 50%
  participants: number | null;
  withSales: number | null;
  invited: number | null;
  started: number | null;
  paidOrders: number | null;
  bagsSold: number | null;
  startAt: string | null;
  endAt: string | null;
  publishedAt: string | null;
  storeUrl: string | null;
  teamId: string | null;
  teamName: string | null;
  repName: string | null;
  salesRepUserId: string | null;
  salesRep: { id: string; displayName: string | null } | null;
}

export interface OrganizationWithPeople extends Organization {
  sports: SportWithTeams[];
  people: LinkedPerson[]; // Org-level people (no sport/team)
  customerStatus: CustomerStatus;
  lastFundraiserLaunchedAt: string | null;
  lastFundraiserTeamDisplay: string | null;
  lastFundraiserAmountCents: string | null;
  lifetimeFundraiserCount: number;
  lifetimeTotalRaisedCents: string;     // BigInt as string
  currentSchoolYearFundraiserCount: number;
  priorSchoolYearFundraiserCount: number;
  blastFundraisers: BlastFundraiser[];
  munchFundCampaigns?: MunchFundCampaign[];
  orgDonationRollups?: OrgDonationRollupView[];
  // Org Snapshot recency aggregates (from GET /api/organizations/:id).
  lastVisitAt: string | null;   // most recent COMPLETED IN_PERSON activity
  lastTouchAt: string | null;   // most recent COMPLETED activity of any type
  // Mirrors the list endpoint's assignedRep projection (active NamedAccount owner).
  // Used by OrganizationDetail to derive the read-only banner + edit gating.
  assignedRep?: { id: string; displayName: string | null } | null;
}

// ==================== PERSON TYPES ====================

// Person list item (for table display)
export interface PersonListItem {
  id: string;
  blastUserId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  crmFirstName: string | null;
  crmLastName: string | null;
  crmEmail: string | null;
  crmPhone: string | null;
  position: string | null;
  temperature: Temperature | null;
  stage: Stage;
  status: string | null;
  source: string | null;
  lastActivityAt: string | null;
  nextFollowUpAt: string | null;
  assignedUserId: string | null;
  manuallyAssigned: boolean;
  assignedUser: {
    id: string;
    displayName: string | null;
    email: string;
  } | null;
  organizations: Array<{
    organization: {
      id: string;
      name: string;
      city: string | null;
      state: string | null;
      customerStatus: CustomerStatus;
    };
    role: string | null;
    sport: SportSummary | null;
    team: TeamSummary | null;
  }>;
  createdAt: string;
  updatedAt: string;
  mergedIntoId?: string | null;
}

// Person detail (full data with organizations)
export interface PersonDetail {
  id: string;
  blastUserId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  crmFirstName: string | null;
  crmLastName: string | null;
  crmEmail: string | null;
  crmPhone: string | null;
  position: string | null;
  twitterHandle: string | null;
  instagramHandle: string | null;
  linkedInUrl: string | null;
  source: string | null;
  lastActivityAt: string | null;
  temperature: Temperature | null;
  stage: Stage;
  status: string | null;
  personalNote?: string | null;
  nextFollowUpAt: string | null;
  departedAt?: string | null; // CRM-110: set = "departed — school unknown" (governance-confirmed)
  assignedUserId: string | null;
  manuallyAssigned: boolean;
  assignedUser: {
    id: string;
    displayName: string | null;
    email: string;
  } | null;
  mineSource: 'direct' | 'derived' | null;
  /** Server-computed: nobody claims this person (no direct assignee, no derived
   *  NamedAccount owner) → editable by any rep (unclaimed-open rule). */
  isUnclaimed?: boolean;
  mineDerivedFrom: { organizationId: string; organizationName: string } | null;
  organizations: Array<{
    id: string;
    role: string | null;
    isPrimary: boolean;
    organization: {
      id: string;
      name: string;
      city: string | null;
      state: string | null;
      address: string | null;
      zip: string | null;
      type: OrganizationType;
      blastSchoolId: string | null;
      customerStatus: CustomerStatus;
    };
    sport: SportSummary | null;
    team: TeamSummary | null;
  }>;
  isActive: boolean;
  convertedAt: string | null;
  createdAt: string;
  updatedAt: string;
  mergedIntoId?: string | null;
}

// Person segment counts (from /api/people/counts)
export interface PersonCounts {
  total: number;
  hot: number;
  followUpDue: number;
  inBlast: number;
  newThisWeek: number;
  noActivity30d: number;
  // Per-CustomerStatus counts: distinct active people whose primary
  // PersonSportTeam link points to an active org in that state.
  PROSPECT: number;
  ONBOARDED: number;
  ACTIVE: number;
  SLIPPING: number;
  LAPSED: number;
}

// Person filters for list query
export interface PersonFilters {
  search?: string;
  stage?: Stage;
  temperature?: Temperature;
  assignedUserId?: string;
  organizationId?: string;
  source?: string;
  inBlast?: boolean;
  isActive?: boolean;
  segment?: string;
  customerStatus?: CustomerStatus[];
  sport?: string;
  role?: string;
  state?: string[];
  sortBy?: 'name' | 'lastActivityAt' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

// Person create payload
export interface PersonCreate {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  crmFirstName?: string | null;
  crmLastName?: string | null;
  crmEmail?: string | null;
  crmPhone?: string | null;
  position?: string | null;
  twitterHandle?: string | null;
  instagramHandle?: string | null;
  linkedInUrl?: string | null;
  source?: string | null;
  temperature?: Temperature | null;
  stage?: Stage;
  status?: string | null;
  nextFollowUpAt?: string | null;
  assignedUserId?: string | null;
}

// Person update payload
export interface PersonUpdate {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  crmFirstName?: string | null;
  crmLastName?: string | null;
  crmEmail?: string | null;
  crmPhone?: string | null;
  position?: string | null;
  twitterHandle?: string | null;
  instagramHandle?: string | null;
  linkedInUrl?: string | null;
  source?: string | null;
  temperature?: Temperature | null;
  stage?: Stage;
  status?: string | null;
  personalNote?: string | null;
  nextFollowUpAt?: string | null;
  assignedUserId?: string | null;
  manuallyAssigned?: boolean;
  blastUserId?: string | null;
}

// ==================== ACTIVITY TYPES ====================

// Activity list item (from API response)
export interface Activity {
  id: string;
  type: ActivityType;
  summary: string;
  activityAt: string;
  createdAt: string;
  createdBy: {
    id: string;
    displayName: string | null;
    email: string;
  };
  status: ActivityStatus;
  scheduledAt: string | null;
  completedAt: string | null;
  cancelReason: string | null;
  // Email-specific fields (only present for EMAIL type activities)
  emailDirection?: EmailDirection;
  emailSubject?: string;
  emailFrom?: string;
  emailTo?: string;
  emailSnippet?: string;
  gmailMessageId?: string;
  gmailThreadId?: string;
  rfc822MessageId?: string;
  isAutoLogged?: boolean;
  meetingLocation?: MeetingLocation | null;
  hasLocation?: boolean; // derived server-side: true when coordinates were captured (IN_PERSON badge)
  // Detail-view-only fields (present on GET /api/activities/:id; CRM-29)
  callDisposition?: CallDisposition | null;
  latitude?: number | null;        // present only for manager/admin or the creator
  longitude?: number | null;
  locationAccuracy?: number | null;
  verification?: {
    state: 'VERIFIED' | 'UNVERIFIED' | 'NEEDS_REVIEW';
    distanceMiles: number | null;
    reason?: 'NO_GPS' | 'NO_ORG_GEO';
  } | null;
  person?: {
    id: string;
    firstName: string;
    lastName: string;
    crmFirstName: string | null;
    crmLastName: string | null;
    position: string | null;
    temperature: Temperature | null;
    organizationId: string | null;
    organizationName: string | null;
  };
}

// Activity create payload
export interface ActivityCreate {
  type: ActivityType;
  summary: string;
  activityAt?: string; // ISO date string, defaults to now
  temperature?: Temperature | null; // Optional: update person's temperature
  latitude?: number;
  longitude?: number;
  locationAccuracy?: number;
  meetingLocation?: MeetingLocation;
  idempotencyKey?: string;
}

// Activity list response from API
export interface ActivityListResponse {
  activities: Activity[];
  pagination: PaginationMeta;
  canEdit: boolean;
}

export interface ScheduleActivityCreate {
  type: ActivityType;
  summary?: string;
  scheduledAt: string;
  status: 'PLANNED';
  idempotencyKey?: string;
}

export interface CompleteActivityPayload {
  type?: ActivityType;
  summary: string;
  activityAt?: string;
  temperature?: Temperature | null;
  meetingLocation?: MeetingLocation;
  latitude?: number;
  longitude?: number;
  locationAccuracy?: number;
}

// Activity create response from API (includes updated person)
export interface ActivityCreateResponse {
  activity: Activity;
  person: {
    id: string;
    temperature: Temperature | null;
    lastActivityAt: string | null;
  };
}

// ==================== TASK TYPES ====================

// Task list item (from API response)
export interface Task {
  id: string;
  title: string;
  description: string | null;
  dueDate: string;
  status: TaskStatus;
  meta?: CoachMoveNotifyMeta | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  person: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    crmFirstName: string | null;
    crmLastName: string | null;
    crmEmail?: string | null;
    crmPhone?: string | null;
  } | null;
  assignedUser: {
    id: string;
    displayName: string | null;
    email: string;
  };
  createdBy: {
    id: string;
    displayName: string | null;
    email: string;
  };
}

// Task filters for list query
export interface TaskFilters {
  status?: TaskStatus;
  assignedUserId?: string; // 'me' for current user
  personId?: string;
  dueBefore?: string; // ISO date
  dueAfter?: string; // ISO date
  includeCompleted?: boolean;
}

// Task create payload
export interface TaskCreate {
  title: string;
  description?: string | null;
  dueDate: string; // ISO date
  personId?: string | null;
  assignedUserId?: string; // Defaults to current user
}

// Task update payload
export interface TaskUpdate {
  title?: string;
  description?: string | null;
  dueDate?: string;
  personId?: string | null;
  assignedUserId?: string;
  status?: TaskStatus;
}

// Task list response from API
export interface TaskListResponse {
  tasks: Task[];
  pagination: PaginationMeta;
}

// Grouped tasks for display (by due date category)
export interface GroupedTasks {
  overdue: Task[];
  today: Task[];
  thisWeek: Task[];
  later: Task[];
}

// ==================== DASHBOARD TYPES ====================

// Recent activity for dashboard display
export interface DashboardActivity {
  id: string;
  type: ActivityType;
  summary: string;
  activityAt: string;
  person: {
    id: string;
    firstName: string;
    lastName: string;
    crmFirstName: string | null;
    crmLastName: string | null;
  };
  createdBy: {
    id: string;
    displayName: string | null;
  };
}

// Upcoming task for dashboard display
export interface DashboardTask {
  id: string;
  title: string;
  dueDate: string;
  person: {
    id: string;
    firstName: string;
    lastName: string;
    crmFirstName: string | null;
    crmLastName: string | null;
  } | null;
}

// Pipeline counts by stage
export interface PipelineCounts {
  LEAD: number;
  PROSPECT: number;
  CUSTOMER: number;
  CHURNED: number;
}

// Temperature counts
export interface TemperatureCounts {
  HOT: number;
  WARM: number;
  COLD: number;
  FREEZING: number;
  unset: number;
}

// Upcoming scheduled activity for dashboard display
export interface DashboardUpcomingActivity {
  id: string;
  type: ActivityType;
  summary: string;
  scheduledAt: string;
  status: ActivityStatus;
  isOverdue: boolean;
  person: {
    id: string;
    firstName: string;
    lastName: string;
    crmFirstName: string | null;
    crmLastName: string | null;
  };
  createdBy: {
    id: string;
    displayName: string;
  };
}

// Full dashboard stats response from API
export interface DashboardStats {
  pipeline: PipelineCounts;
  temperature: TemperatureCounts;
  recentActivities: DashboardActivity[];
  upcomingTasks: DashboardTask[];
  upcomingActivities: DashboardUpcomingActivity[];
  totalPeople: number;
  peopleWithActivity7Days: number;
}

// ==================== GMAIL TYPES ====================

// Gmail connection status response from /api/gmail/status
export interface GmailConnectionStatus {
  connected: boolean;
  connection: {
    googleEmail: string;
    syncStatus: SyncStatus;
    syncError: string | null;
    lastSyncAt: string | null;
    connectedAt: string;
    calendarSyncEnabled: boolean;
    needsCalendarReconnect: boolean;
    canSend?: boolean;
  } | null;
}

// Gmail connection item (admin view) from /api/gmail/connections
export interface GmailConnectionItem {
  id: string;
  googleEmail: string;
  syncStatus: SyncStatus;
  syncError: string | null;
  lastSyncAt: string | null;
  isActive: boolean;
  createdAt: string;
  user: {
    id: string;
    displayName: string | null;
    email: string;
  };
}

// ==================== BLAST INTEGRATION TYPES ====================

// Blast school from GET /api/integration/schools
export interface BlastSchool {
  id: number;
  name: string;
  city: string;
  state: string;
  school_type: string;
  nces_id: string | null;
  district: string | null;
  timezone: string;
}

// Blast team from GET /api/integration/teams
export interface BlastTeam {
  id: number;
  name: string;
  sport_id: number;
  level: string;
  level_name: string;
  gender: string;
  school_year: number;
}

// Enriched Blast teams response (current/past split with sport metadata)
export interface EnrichedBlastTeam {
  id: number;
  name: string;
  level: string;
  levelName: string;
  gender: string;
  schoolYear: number;
}

export interface EnrichedBlastTeamWithSport extends EnrichedBlastTeam {
  sportId: number;
  sportName: string;
  season: string | null;
}

export interface BlastCurrentSportGroup {
  sportId: number;
  sportName: string;
  season: string | null;
  teams: EnrichedBlastTeam[];
}

export interface BlastPastYearGroup {
  schoolYear: number;
  displayYear: string;
  teams: EnrichedBlastTeamWithSport[];
}

export interface EnrichedBlastTeamsResponse {
  current: BlastCurrentSportGroup[];
  past: BlastPastYearGroup[];
  summary: {
    currentTeamCount: number;
    pastTeamCount: number;
    yearRange: string;
    sportCount: number;
  };
}

// Approved-teams option sets (from GET /api/integration/approved-teams)
export interface ApprovedTeamsResponse {
  sports: { blastSportId: number; name: string }[];
  levelsBySport: Record<number, string[]>;
  gendersBySport: Record<number, string[]>;
}

// Blast user from GET /api/integration/users
export interface BlastUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
}

// CRM-side convert request (what the modal sends to our backend)
export interface ConvertRequest {
  school: {
    blast_school_id?: number;
    name: string;
    city: string;
    state: string;
    address?: string;
    zip?: string;
    school_type?: string;
    timezone?: string;
  };
  team: {
    blast_team_id?: number;
    sport_name?: string;
    level?: string;
    gender?: string;
    school_year?: number;
  };
  coach: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  };
  crmPersonId?: string;
}

// Convert response from Blast (passed through by backend)
export interface ConvertResponse {
  school: { id: number; name: string };
  team: { id: number; name: string | null };
  coach: { id: number | null; email: string; invited: boolean };
}

// BlastSport for sport dropdown
export interface BlastSport {
  blastSportId: number;
  name: string;
  season: string | null;
}

// ==================== TERRITORY MANAGEMENT ====================

// Territory Management Enums
export type TerritoryType = 'STATE' | 'ZIP_CODE' | 'HYBRID';
export type NamedAccountSource = 'BLAST_SYNC' | 'MANUAL';
export type AssignmentRuleType = 'NAMED_ACCOUNT' | 'TERRITORY_ZIP' | 'TERRITORY_STATE' | 'REGION_FALLBACK' | 'UNASSIGNED_POOL';
export type OwnerSource = 'named_account' | 'territory_zip' | 'territory_state' | 'region_fallback' | 'unassigned';

// Region
export interface Region {
  id: string;
  name: string;
  code: string;
  description: string | null;
  color: string;
  sortOrder: number;
  isActive: boolean;
  territories?: Territory[];
  _count?: { territories: number };
}

// Territory
export interface Territory {
  id: string;
  name: string;
  regionId: string;
  assignedUserId: string | null;
  territoryType: TerritoryType;
  isActive: boolean;
  region?: Region;
  assignedUser?: { id: string; displayName: string } | null;
  states?: { stateCode: string }[];
  zipCodes?: { zipCode: string }[];
  _count?: { schoolCount: number };
}

// Named Account
export interface NamedAccount {
  id: string;
  organizationId: string;
  assignedUserId: string;
  source: NamedAccountSource;
  assignedAt: string;
  assignedById: string | null;
  isActive: boolean;
  organization?: { id: string; name: string; state: string | null; blastSchoolId: string | null };
  assignedUser?: { id: string; displayName: string };
  assignedBy?: { id: string; displayName: string } | null;
}

// Owner Resolution
export interface OwnerResolution {
  userId: string | null;
  displayName: string | null;
  source: OwnerSource;
  territoryId?: string;
  territoryName?: string;
  regionId?: string;
  regionName?: string;
}

// Assignment Rule
export interface AssignmentRule {
  id: string;
  name: string;
  description: string | null;
  ruleType: AssignmentRuleType;
  priority: number;
  isActive: boolean;
}

// Coverage Stats
export interface CoverageStats {
  totalSchools: number;
  namedAccounts: number;
  regionCount: number;
  byRegion: { regionId: string; regionName: string; regionColor: string; territoryCount: number; stateCount: number; zipCount: number }[];
}

// Map Data
export interface StateCoverageEntry {
  regionId: string;
  regionColor: string;
  regionName: string;
  territories: { id: string; name: string; repName: string | null }[];
}

export interface MapData {
  regions: MapRegion[];
  stateCoverage: Record<string, StateCoverageEntry>;
}

export interface MapRegion {
  id: string;
  name: string;
  code: string;
  color: string;
  territories: MapTerritory[];
}

export interface MapTerritory {
  id: string;
  name: string;
  assignedRep: { id: string; displayName: string } | null;
  boundaryType: TerritoryType;
  states: string[];
  zipCodes: string[];
}

// Realignment
export interface RealignmentPreview {
  token: string;
  expiresAt: string;
  changes: { territoryId: string; territoryName: string; beforeStates: string[]; afterStates: string[]; beforeZips: string[]; afterZips: string[] }[];
}

// Create/Update payloads
export interface RegionCreate { name: string; code: string; color: string; description?: string; sortOrder?: number }
export interface RegionUpdate { name?: string; code?: string; color?: string; description?: string; sortOrder?: number }
export interface TerritoryCreate { name: string; regionId: string; assignedUserId?: string; territoryType: TerritoryType; states?: string[]; zipCodes?: string[] }
export interface TerritoryUpdate { name?: string; regionId?: string; assignedUserId?: string | null }
export interface NamedAccountCreate { organizationId: string; assignedUserId: string }

// Filters
export interface TerritoryFilters { regionId?: string; assignedUserId?: string; unassigned?: boolean }
export interface NamedAccountFilters { organizationId?: string; assignedUserId?: string; source?: NamedAccountSource }

// ==================== STATE DRILL-DOWN TYPES ====================

export type SchoolStatus = 'blast' | 'competitor' | 'open';

export interface StateDetailSchool {
  id: string;
  name: string;
  city: string | null;
  lat: number;
  lng: number;
  status: SchoolStatus;
  repName: string | null;
  competitorPlatform: CompetitorPlatform | null;
}

export interface StateDetailTerritory {
  id: string;
  name: string;
  repName: string | null;
  boundaryType: TerritoryType;
  zipPrefixes: string[];
  schoolCount: number;
  blastCount: number;
  competitorCount: number;
  openCount: number;
}

export interface StateDetail {
  state: { code: string; name: string };
  region: { id: string; name: string; color: string } | null;
  stats: { totalSchools: number; blastCount: number; competitorCount: number; openCount: number };
  territories: StateDetailTerritory[];
  schools: StateDetailSchool[];
}

// ==================== TERRITORY ANALYSIS TYPES ====================

export interface AnalyzeSettings {
  radiusMiles: number;
  minSchools: number;
  maxBlastPct: number;
  weightMode: number;
  includeCompetitor: boolean;
}

export const DEFAULT_ANALYZE_SETTINGS: AnalyzeSettings = {
  radiusMiles: 75,
  minSchools: 40,
  maxBlastPct: 15,
  weightMode: 0.5,
  includeCompetitor: true,
};

export interface SuggestedTerritory {
  zipPrefixes: string[];
  estimatedSchools: number;
  nearestExistingTerritory: string | null;
}

export interface OpportunityCluster {
  id: string;
  rank: number;
  centerLat: number;
  centerLng: number;
  radiusMiles: number;
  label: string;
  totalSchools: number;
  blastCount: number;
  competitorCount: number;
  openCount: number;
  penetrationPct: number;
  opportunityScore: number;
  suggestedTerritory: SuggestedTerritory;
}

export interface StateHeatmapEntry {
  stateCode: string;
  totalSchools: number;
  blastCount: number;
  competitorCount: number;
  openCount: number;
  penetrationPct: number;
  opportunityScore: number;
}

export interface AnalyzeNationalStats {
  totalSchools: number;
  blastCount: number;
  competitorCount: number;
  openCount: number;
  avgPenetration: number;
  medianPenetration: number;
}

export interface AnalyzeResponse {
  nationalStats: AnalyzeNationalStats;
  stateHeatmap: StateHeatmapEntry[];
  clusters: OpportunityCluster[];
}

// ==================== CLUSTER DETAIL TYPES ====================

export interface ClusterDetail {
  cluster: {
    label: string;
    centerLat: number;
    centerLng: number;
    radiusMiles: number;
  };
  stats: { totalSchools: number; blastCount: number; competitorCount: number; openCount: number };
  states: string[];
  territories: StateDetailTerritory[];
  schools: StateDetailSchool[];
}

// ───── Global Search ─────────────────────────────────────────────────

export interface SearchPersonResult {
  id: string;
  firstName: string;
  lastName: string;
  position: string | null;
  organization: { id: string; name: string } | null;
  // Resolved by the backend per the search-match rule (CRM-89): the value that
  // matched the query when the rep searched that kind of value, else the
  // preferred value (crmPhone || phone, crmEmail || email).
  phone: string | null;
  email: string | null;
}

export interface SearchOrganizationResult {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  type: 'SCHOOL' | 'CLUB_TEAM';
}

export interface SearchTaskResult {
  id: string;
  title: string;
  dueDate: string | null;
  status: string;
  person: { id: string; firstName: string; lastName: string } | null;
}

export interface GlobalSearchResults {
  people?: SearchPersonResult[];
  organizations?: SearchOrganizationResult[];
  tasks?: SearchTaskResult[];
}

export interface GlobalSearchResponse {
  query: string;
  results: GlobalSearchResults;
  counts: Partial<Record<'people' | 'organizations' | 'tasks', number>>;
}

export type SearchEntityType = 'people' | 'organizations' | 'tasks';

// Inside-Sales Call Queue
export type CallDisposition =
  | 'NO_ANSWER'
  | 'VOICEMAIL_LEFT'
  | 'NOT_INTERESTED'
  | 'DEMO_BOOKED'
  | 'WRONG_NUMBER'
  | 'CALLBACK_LATER'
  | 'ALREADY_CUSTOMER'
  | 'GATEKEEPER'
  | 'DO_NOT_CALL';

export type CallQueueItemStatus = 'PENDING' | 'CALLED' | 'CALLBACK_SCHEDULED' | 'SKIPPED';

export interface QueueFilters {
  sport?: string;
  role?: string;
  state?: string[];
  customerStatus?: CustomerStatus[];
}

export interface CallQueue {
  id: string;
  name: string;
  ownerId: string;
  filtersJson: QueueFilters;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { items: number };
}

export interface CallQueueItemPerson {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  crmPhone: string | null;
  position: string | null;
  organizations?: Array<{
    sport: { name: string } | null;
    organization: { id: string; name: string; city: string | null; state: string | null };
    role: string | null;
    isPrimary: boolean;
  }>;
  activities?: Array<{
    id: string;
    type: string;
    summary: string;
    activityAt: string;
    createdBy?: { displayName: string | null; email: string };
  }>;
}

export interface CallQueueItem {
  id: string;
  callQueueId: string;
  personId: string;
  status: CallQueueItemStatus;
  disposition: CallDisposition | null;
  dispositionAt: string | null;
  callbackAt: string | null;
  attemptCount: number;
  notes: string | null;
  enrolledAt: string;
  person?: CallQueueItemPerson;
}

export interface CallQueueSummary {
  total: number;
  byStatus: Record<CallQueueItemStatus, number>;
  byDisposition: Record<CallDisposition, number>;
}

export interface DispositionPayload {
  disposition: CallDisposition;
  summary?: string;
  notes?: string;
  callbackAt?: string;
  demoAt?: string;
}

// ==================== SEGMENT COUNTS TYPES ====================

// People segment counts (from /api/people/segment-counts)
export interface PeopleSegmentCounts {
  scopeTotal: number;
  segmentCounts: {
    hot: number;
    inBlast: number;
    followUpDue: number;
    new: number;
    noActivity30d: number;
  };
}

// Organizations segment counts (from /api/organizations/segment-counts)
export interface OrganizationsSegmentCounts {
  scopeTotal: number;
  segmentCounts: {
    inBlast: number;
    churned: number;
    neverVisited: number;
    hasCoaches: number;
  };
}

// Cockpit (Sales Manager Dashboard) ----------------------------------------
export type CockpitScope = 'mine' | 'team';
export type CockpitPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year';

export interface CockpitAdoption { provisioned: number; onboarded: number; activeThisPeriod: number; goneDark: number; }
export interface CockpitActivityPulse {
  total: number;
  byType: Record<ActivityType, number>;
  onsite: number;
  onsiteWithGps: number;
  onsiteVerified: number;
  onsiteUnverified: number;
  onsiteFlagged: number;
  perDay: Array<{ date: string; count: number }>;
}
export interface FundraisingWindowPair { received: string; launched: string; }
export interface FundraisingWindows {
  calYtd: FundraisingWindowPair; priorCalYtd: FundraisingWindowPair;
  sy: FundraisingWindowPair; priorSy: FundraisingWindowPair;
  campaignsCalYtd: number; campaignsSy: number;
}

/** Team-school-year fundraising figures (CRM-120) — cents as decimal strings,
 *  year fields in Blast's END-year convention (2027 = the 2026-27 season). */
export interface CockpitSeason {
  current: string;
  prior: string;
  campaignsCurrent: number;
  campaignsPrior: number;
  currentSeasonYear: number;
  priorSeasonYear: number;
}

/** The bounds the fundraising money was actually cut at (CRM-121), ISO strings.
 *  Sent by the server so the UI captions each figure with the real window rather
 *  than recomputing one here and drifting from the money. `asOf` / `priorAsOf`
 *  are the season cut dates — a season is a set of teams, not a date range, so
 *  its caption names the season and the date it was counted through. */
export interface CockpitFundraisingWindowBounds {
  calYtdStart: string; calYtdEnd: string;
  priorCalYtdStart: string; priorCalYtdEnd: string;
  syStart: string; syEnd: string;
  priorSyStart: string; priorSyEnd: string;
  asOf: string;
  priorAsOf: string;
}

export type UnattributedReason = 'NO_CRM_USER' | 'DEACTIVATED' | 'NO_REP_ROW';

export interface CockpitUnattributedContributor {
  key: string;
  displayName: string | null;
  blastUserId: string | null;   // null for NO_REP_ROW
  reason: UnattributedReason;
  campaigns: number;
  windows: FundraisingWindows;
}

/** Fundraising credited to someone with no row on the Team page (former or
 *  deactivated rep). All-Teams scope only; null when a team is selected. */
export interface CockpitUnattributed {
  campaigns: number;
  windows: FundraisingWindows;
  contributors: CockpitUnattributedContributor[];
  season?: CockpitSeason;
}

export interface CockpitRepRow {
  userId: string;
  displayName: string | null;
  lastSeen: string | null;
  activitiesTotal: number;
  byType: Record<ActivityType, number>;
  onsite: number;
  onsiteVerified: number;
  onsiteUnverified: number;
  onsiteFlagged: number;
  contactsTouched: number;
  overdue: number;
  conversions: number;
  pipeline: PipelineCounts;
  peopleOwned: number;
  namedAccountsOwned: number;
  raisedThisSyCents: string;
  raisedPriorSyCents: string;
  fundraiserYoyPct: number | null;
  campaignsThisSy: number;
  avgParticipationThisSy: number | null;
  windows?: FundraisingWindows;
  season?: CockpitSeason;
}
export interface OnsiteFlagRow {
  activityId: string;
  activityAt: string;
  repId: string;
  repName: string | null;
  personId: string;
  personName: string;
  orgId: string | null;
  orgName: string | null;
  distanceMiles: number | null;
  locationAccuracyM: number | null;
}
export interface CockpitSlippingOrg {
  organizationId: string;
  name: string;
  owner: string | null;
  lastFundraiserLaunchedAt: string | null;
  lastFundraiserAmountCents: string | null;
}
export interface CockpitCustomerHealth {
  counts: Record<CustomerStatus, number>;
  lifetimeRaisedCents: string;
  slippingList: CockpitSlippingOrg[];
}
export interface CockpitHygiene { overdueTasks: number; staleProspects: number; upcomingActivities: number; }

export interface CockpitTeamFundraising {
  raisedThisSyCents: string;
  raisedPriorSyCents: string;
  yoyPct: number | null;
  campaignsThisSy: number;
  campaignsPriorSy: number;
  avgParticipationThisSy: number | null;
  avgPerCampaignThisSyCents: string;
  trend: Array<{ label: string; raisedCents: string }>;
  windows: FundraisingWindows;
  season?: CockpitSeason;
}

export interface CockpitRepFundraiserCampaign {
  blastFundraiserId: string;
  organizationId: string;
  organizationName: string | null;
  launchedAt: string | null;
  raisedCents: string;
  participationPct: number | null;
  athleteCount: number | null;
}

export interface CockpitRepFundraiserDetail {
  trend: Array<{ label: string; raisedCents: string }>;
  campaigns: CockpitRepFundraiserCampaign[];
}

export interface CockpitResponse {
  scope: CockpitScope;
  teamId: string | null;
  period: { from: string; to: string };
  fundraisingWindows?: CockpitFundraisingWindowBounds;
  adoption: CockpitAdoption | null;
  activityPulse: CockpitActivityPulse;
  repRows: CockpitRepRow[] | null;
  unattributed: CockpitUnattributed | null;
  scorecard: CockpitRepRow | null;
  pipeline: PipelineCounts;
  temperature: TemperatureCounts;
  conversions: number;
  customerHealth: CockpitCustomerHealth;
  teamFundraising: CockpitTeamFundraising | null;
  hygiene: CockpitHygiene;
}

// ===== Rep "Today" dashboard (GET /api/dashboard/today) — mirrors the backend service =====
export interface TodayGlance {
  visitsToday: number;
  callsToday: number;
  tasksDueToday: number;
  overdueCount: number;
  loggedToday: number;
}

export interface TodayAgendaPerson {
  id: string;
  firstName: string | null;
  lastName: string | null;
  crmFirstName: string | null;
  crmLastName: string | null;
}

export interface TodayAgendaItem {
  kind: 'task' | 'activity';
  id: string;
  bucket: 'overdue' | 'today';
  title: string;
  activityType?: ActivityType;
  at: string | null;
  isPast: boolean;
  person: TodayAgendaPerson | null;
  org: { id: string; name: string } | null;
  meetingLocation: MeetingLocation | null;
  /** Coach-change heads-up task — rows deep-link to the prefilled review flow. */
  isCoachMoveReport?: boolean;
}

export interface TodayAttention {
  hot: number;
  followUpDue: number;
  atRisk: number;
  coachChangeReports: number;
  /** When exactly one report is open, its coach — banner fast-paths to the prefilled review. */
  coachChangeSoloPersonId: string | null;
}

export type TodayJumpKind = 'PERSON' | 'SCHOOL' | 'CLUB_TEAM' | 'PROGRAM';
export interface TodayJumpItem {
  kind: TodayJumpKind;
  id: string;
  name: string;
  subtitle?: string;
}

export interface TodayFundraiserCampaign {
  id: string;
  organizationId: string;
  organizationName: string | null;
  raisedCents: string;
  launchedAt: string | null;
}

export interface TodayFundraising {
  raisedThisSyCents: string;
  raisedPriorSyCents: string;
  yoyPct: number | null;
  campaignsThisSy: number;
  topCampaigns: TodayFundraiserCampaign[];
  windows: FundraisingWindows;
  /** Team's-school-year figures — the same numbers the Team page shows for this rep. */
  season: CockpitSeason;
  /** The real cut dates the money was computed at, for the window caption. */
  fundraisingWindows: CockpitFundraisingWindowBounds;
}

export interface TodayResponse {
  generatedAt: string;
  glance: TodayGlance;
  fundraising: TodayFundraising;
  agenda: TodayAgendaItem[];
  attention: TodayAttention;
  jumpBackIn: { favorites: TodayJumpItem[]; recent: TodayJumpItem[] };
}

// ==================== COACH MOVE FLAGS (CRM-110) ====================
export type CoachMoveReason = 'LEFT' | 'RETIRED' | 'MOVED' | 'WRONG_SCHOOL';
export type CoachMoveDiscoveryMethod =
  | 'NEW_COACH_DIRECT' | 'COACH_DIRECT' | 'SCHOOL_STAFF' | 'SCHOOL_VISIT' | 'UNREACHABLE' | 'SECONDHAND' | 'HUNCH';
export type CoachMoveFlagStatus =
  | 'RAISED' | 'ON_CALL_LIST' | 'CONFIRMED' | 'APPLIED' | 'KICKED_BACK' | 'REJECTED' | 'REVERSED';
export type CoachMoveDecideAction = 'APPROVE' | 'CHANGE' | 'KICK_BACK' | 'REJECT';

export interface CoachMoveFlagItem {
  id: string;
  personId: string;
  fromOrganizationId: string;
  reason: CoachMoveReason;
  discoveryMethod: CoachMoveDiscoveryMethod;
  sourceStrength: 'STRONG' | 'MEDIUM' | 'WEAK';
  status: CoachMoveFlagStatus;
  toOrganizationId: string | null;
  toSchoolText: string | null;
  note: string | null;
  decisionNote: string | null;
  createdAt: string;
  person?: { id: string; firstName: string; lastName: string; crmFirstName: string | null; crmLastName: string | null; position: string | null };
  fromOrganization?: { id: string; name: string; city: string | null; state: string | null };
  toOrganization?: { id: string; name: string; city: string | null; state: string | null } | null;
  raisedBy?: { id: string; displayName: string | null };
  verifiedBy?: { id: string; displayName: string | null } | null;
  decidedBy?: { id: string; displayName: string | null } | null;
}

/** Structured evidence stored on a coach-change heads-up Task (Task.meta). */
export interface CoachMoveNotifyMeta {
  kind: 'COACH_MOVE_NOTIFY';
  reason: CoachMoveReason;
  discoveryMethod: CoachMoveDiscoveryMethod;
  fromOrganizationId: string;
  toOrganizationId: string | null;
  toOrganizationName: string | null;
  toSchoolText: string | null;
  note: string | null;
  reportedById: string;
}

/** Open non-owner heads-up (a PENDING Task carrying coach-move evidence). */
export interface CoachMoveNotifyItem {
  id: string;
  createdAt: string;
  meta?: CoachMoveNotifyMeta | null;
  assignedUser: { id: string; displayName: string | null };
  createdBy: { id: string; displayName: string | null };
}

export interface TrustedRep {
  id: string;
  displayName: string | null;
  email: string;
  trustedForAutoApply: boolean;
}

export type PersonMergeStatus = 'RAISED' | 'APPLIED' | 'REJECTED' | 'REVERSED';
export type PersonMergeDecideAction = 'APPROVE' | 'REJECT' | 'SWAP';
export interface PersonMergeParty { id: string; firstName: string; lastName: string; crmFirstName: string | null; crmLastName: string | null; email: string | null; blastUserId: string | null }
export interface PersonMergeItem {
  id: string; survivorId: string; loserId: string; status: PersonMergeStatus; reason: string | null; decisionNote: string | null;
  blastConflict: boolean; preservedFields: Record<string, unknown> | null; appliedAt: string | null; createdAt: string;
  survivor?: PersonMergeParty; loser?: PersonMergeParty;
  raisedBy?: { id: string; displayName: string | null }; decidedBy?: { id: string; displayName: string | null } | null;
}
export interface MergePreviewSide {
  id: string; firstName: string; lastName: string; crmFirstName: string | null; crmLastName: string | null;
  email: string | null; crmEmail: string | null; phone: string | null; crmPhone: string | null; position: string | null;
  blastUserId: string | null; createdAt: string;
  schools: Array<{ organizationId: string; name: string; role: string | null; isPrimary: boolean }>;
  counts: { activities: number; tasks: number; lists: number };
}
export interface MergePreview { survivor: MergePreviewSide; loser: MergePreviewSide; swapAllowed: boolean; blastConflict: boolean; outcome: { filled: Record<string, unknown>; preserved: Record<string, unknown> }; error: string | null }
export interface MergedIntoInfo { id: string; name: string | null; mergedAt: string | null; by: string | null }

export * from './tag';
export * from './lead';
